(() => {
  const ROOT_SELECTOR = '[data-vzy-collection-bands]';
  const MOBILE_QUERY = window.matchMedia('(max-width: 749px)');
  const REDUCED_MOTION_QUERY = window.matchMedia('(prefers-reduced-motion: reduce)');

  if (window.VzyCollectionBands) {
    window.VzyCollectionBands.init();
    return;
  }

  const collator = new Intl.Collator('ro', {
    numeric: true,
    sensitivity: 'base',
  });

  function emitInteraction(root, event, detail = {}) {
    root.dispatchEvent(
      new CustomEvent('vzy:collection:interaction', {
        bubbles: true,
        detail: {
          event,
          sectionId: root.dataset.sectionId || '',
          ...detail,
        },
      }),
    );
  }

  function initRoot(root) {
    if (!root || root.dataset.vzyBandsReady === 'true') return;

    const controller = new AbortController();
    const signal = controller.signal;
    const toolbar = root.querySelector('[data-vzy-toolbar]');
    const isHome = root.dataset.vzyPageType === 'index';
    const primaryCollectionCount = Number.parseInt(root.dataset.vzyPrimaryCount || '', 10);
    const homeTabs = Array.from(root.querySelectorAll('[data-vzy-home-band-tab]'));
    const categoryFilter = toolbar?.querySelector('[data-vzy-category-filter]');
    const availabilityFilter = toolbar?.querySelector('[data-vzy-availability-filter]');
    const sortControl = toolbar?.querySelector('[data-vzy-sort]');
    const status = toolbar?.querySelector('[data-vzy-filter-status]');
    const resetButtons = Array.from(root.querySelectorAll('[data-vzy-filter-reset]'));
    const emptyState = root.querySelector('[data-vzy-empty-state]');
    const urlParams = new URLSearchParams(window.location.search);

    const bands = Array.from(root.querySelectorAll('[data-vzy-product-band]')).map((band) => {
      const grid = band.querySelector('[data-vzy-band-grid]');
      const items = Array.from(band.querySelectorAll('[data-vzy-band-item]'));
      const toggle = band.querySelector('[data-vzy-band-toggle]');
      const count = band.querySelector('[data-vzy-band-count]');
      const deferred = band.querySelector('template[data-vzy-band-deferred]');

      return {
        band,
        grid,
        items,
        toggle,
        count,
        deferred,
        deferredCount: deferred ? deferred.content.querySelectorAll('[data-vzy-band-item]').length : 0,
        expanded: toggle?.getAttribute('aria-expanded') === 'true',
        showAll: band.dataset.vzyShowAll === 'true',
      };
    });

    if (!bands.length) return;

    // Cards past the first screenful ship inside <template> (inert: not in the DOM,
    // no images). They join the grid when a band expands or a filter/sort needs them.
    const materialize = (bandState) => {
      if (!bandState.deferred) return;
      bandState.deferred.replaceWith(bandState.deferred.content);
      bandState.deferred = null;
      bandState.deferredCount = 0;
      bandState.items = Array.from(bandState.band.querySelectorAll('[data-vzy-band-item]'));
    };

    root.dataset.vzyBandsReady = 'true';
    root.vzyBandsController = controller;

    const requestedCategory = urlParams.get('vzy_family') || urlParams.get('vzy_category') || '';
    const requestedSort = urlParams.get('vzy_sort') || '';
    const categoryExists = Array.from(categoryFilter?.options || []).some(
      (option) => option.value === requestedCategory,
    );
    const sortExists = Array.from(sortControl?.options || []).some(
      (option) => option.value === requestedSort,
    );

    if (!isHome && categoryFilter && categoryExists) categoryFilter.value = requestedCategory;
    if (!isHome && availabilityFilter) availabilityFilter.checked = urlParams.get('vzy_stock') === '1';
    if (!isHome && sortControl && sortExists) sortControl.value = requestedSort;

    const state = {
      category: isHome ? 'all' : categoryFilter?.value || 'all',
      availableOnly: availabilityFilter?.checked || false,
      sort: sortControl?.value || 'recommended',
    };

    const syncUrl = () => {
      if (isHome) return;
      const params = new URLSearchParams(window.location.search);
      if (state.category === 'all') params.delete('vzy_family');
      else params.set('vzy_family', state.category);
      params.delete('vzy_category');

      if (state.availableOnly) params.set('vzy_stock', '1');
      else params.delete('vzy_stock');

      if (state.sort === 'recommended') params.delete('vzy_sort');
      else params.set('vzy_sort', state.sort);

      const query = params.toString();
      window.history.replaceState(
        window.history.state,
        '',
        `${window.location.pathname}${query ? `?${query}` : ''}${window.location.hash}`,
      );
    };

    const getLimit = (band) => {
      const value = MOBILE_QUERY.matches ? band.dataset.initialMobile : band.dataset.initialDesktop;
      return Math.max(1, Number.parseInt(value || '', 10) || Number.MAX_SAFE_INTEGER);
    };

    const compareItems = (a, b) => {
      const aAvailable = a.dataset.vzyAvailable === 'true';
      const bAvailable = b.dataset.vzyAvailable === 'true';

      if (state.sort === 'price-asc' || state.sort === 'price-desc') {
        const aPrice = Number.parseInt(a.dataset.vzyPriceCents || '', 10);
        const bPrice = Number.parseInt(b.dataset.vzyPriceCents || '', 10);
        const aValid = Number.isFinite(aPrice);
        const bValid = Number.isFinite(bPrice);

        if (aValid !== bValid) return aValid ? -1 : 1;
        if (aValid && bValid && aPrice !== bPrice) {
          return state.sort === 'price-asc' ? aPrice - bPrice : bPrice - aPrice;
        }
      }

      if (state.sort === 'title-asc') {
        const titleOrder = collator.compare(a.dataset.vzyTitleSort || '', b.dataset.vzyTitleSort || '');
        if (titleOrder !== 0) return titleOrder;
      }

      if (state.sort === 'recommended' && aAvailable !== bAvailable) {
        return aAvailable ? -1 : 1;
      }

      return (
        Number.parseInt(a.dataset.vzyOriginalIndex || '', 10) -
        Number.parseInt(b.dataset.vzyOriginalIndex || '', 10)
      );
    };

    const render = ({ announce = true } = {}) => {
      if (state.availableOnly || state.sort !== 'recommended') bands.forEach(materialize);
      let matchingProducts = 0;
      const matchingHandles = new Set();
      let visibleBands = 0;

      bands.forEach((bandState) => {
        const { band, grid, items, toggle, count } = bandState;
        const categoryMatches =
          isHome || state.category === 'all' || band.dataset.vzyBandKey === state.category;
        const filteredItems = items.filter(
          (item) => !state.availableOnly || item.dataset.vzyAvailable === 'true',
        );
        const sortedItems = [...items].sort(compareItems);

        grid?.append(...sortedItems);

        const bandHasResults = categoryMatches && filteredItems.length > 0;
        band.hidden = !bandHasResults;

        if (bandHasResults) {
          filteredItems.forEach((item) => {
            const handle = item.dataset.vzyProductHandle || `${band.dataset.vzyBandKey}:${item.dataset.vzyOriginalIndex}`;
            matchingHandles.add(handle);
          });
          matchingProducts = matchingHandles.size;
          visibleBands += 1;
        }

        let visibleIndex = 0;
        const limit = getLimit(band);
        const expanded = bandState.showAll || bandState.expanded;

        sortedItems.forEach((item) => {
          const matchesAvailability = !state.availableOnly || item.dataset.vzyAvailable === 'true';
          const shouldShow =
            bandHasResults &&
            matchesAvailability &&
            (expanded || visibleIndex < limit);

          item.hidden = !shouldShow;
          if (matchesAvailability) visibleIndex += 1;
        });

        const totalItems = filteredItems.length + bandState.deferredCount;
        if (count) {
          count.textContent = `${totalItems} ${totalItems === 1 ? 'model' : 'modele'}`;
        }

        if (toggle) {
          const canExpand = !bandState.showAll && totalItems > limit;
          toggle.hidden = !bandHasResults || !canExpand;
          toggle.setAttribute('aria-expanded', String(bandState.expanded));

          const label = toggle.querySelector('[data-vzy-band-toggle-label]');
          if (label) {
            label.textContent = bandState.expanded
              ? toggle.dataset.labelLess
              : `Vezi toate cele ${totalItems} modele`;
          }
        }
      });

      if (emptyState) emptyState.hidden = matchingProducts > 0;
      if (status) {
        const usePrimaryCollectionCount =
          state.category === 'all' && !state.availableOnly && Number.isFinite(primaryCollectionCount);
        const reportedProducts = usePrimaryCollectionCount ? primaryCollectionCount : matchingProducts;
        const statusText = usePrimaryCollectionCount
          ? `${reportedProducts} ${reportedProducts === 1 ? 'produs' : 'produse'} în colecția principală.`
          : reportedProducts === 1
            ? '1 produs găsit.'
            : `${reportedProducts} produse găsite în ${visibleBands} ${visibleBands === 1 ? 'categorie' : 'categorii'}.`;
        if (status.textContent !== statusText) {
          if (!announce) status.setAttribute('aria-live', 'off');
          status.textContent = statusText;
          if (!announce) requestAnimationFrame(() => status.setAttribute('aria-live', 'polite'));
        }
      }

      const hasActiveFilters =
        state.category !== 'all' || state.availableOnly || state.sort !== 'recommended';
      resetButtons.forEach((button) => {
        button.disabled = !hasActiveFilters;
      });

    };

    const setHomeActiveTab = (bandKey) => {
      homeTabs.forEach((candidate) => {
        const active = candidate.dataset.vzyHomeBandTab === bandKey;
        candidate.classList.toggle('is-active', active);
        if (active) candidate.setAttribute('aria-current', 'location');
        else candidate.removeAttribute('aria-current');
      });
    };

    const activateHomeBand = (bandKey, { focusTab = false, scroll = false } = {}) => {
      const tab = homeTabs.find((candidate) => candidate.dataset.vzyHomeBandTab === bandKey);
      const bandState = bands.find((candidate) => candidate.band.dataset.vzyBandKey === bandKey);
      if (!tab || !bandState) return false;

      setHomeActiveTab(bandKey);

      if (scroll) {
        bandState.band.scrollIntoView({
          behavior: focusTab || REDUCED_MOTION_QUERY.matches ? 'auto' : 'smooth',
          block: 'start',
        });
        window.history.replaceState(
          window.history.state,
          '',
          `${window.location.pathname}${window.location.search}#${bandState.band.id}`,
        );
      }
      if (focusTab) {
        tab.focus({ preventScroll: true });
      }

      const tabStrip = tab.closest('.vzy-home-catalog-tabs');
      if (tabStrip) {
        const stripRect = tabStrip.getBoundingClientRect();
        const tabRect = tab.getBoundingClientRect();
        if (tabRect.left < stripRect.left || tabRect.right > stripRect.right) {
          const centeredDelta =
            tabRect.left - stripRect.left - (tabStrip.clientWidth - tabRect.width) / 2;
          tabStrip.scrollTo({
            left: tabStrip.scrollLeft + centeredDelta,
            behavior: focusTab || REDUCED_MOTION_QUERY.matches ? 'auto' : 'smooth',
          });
        }
      }

      emitInteraction(root, 'select_home_category', {
        band: bandState.band.dataset.vzyBandSlug || '',
      });
      return true;
    };

    if (isHome) {
      const hashTarget = decodeURIComponent(window.location.hash.replace(/^#/, ''));
      const initialBand =
        bands.find((candidate) => candidate.band.id === hashTarget) ||
        bands.find((candidate) => !candidate.band.hidden) ||
        bands[0];
      if (initialBand) setHomeActiveTab(initialBand.band.dataset.vzyBandKey);

      if ('IntersectionObserver' in window) {
        const homeBandObserver = new IntersectionObserver(
          (entries) => {
            const visible = entries
              .filter((entry) => entry.isIntersecting)
              .sort((a, b) => b.intersectionRatio - a.intersectionRatio);
            const current = visible[0]?.target;
            if (current) setHomeActiveTab(current.dataset.vzyBandKey);
          },
          { rootMargin: '-18% 0px -62% 0px', threshold: [0.05, 0.25, 0.5] },
        );
        bands.forEach((candidate) => homeBandObserver.observe(candidate.band));
        signal.addEventListener('abort', () => homeBandObserver.disconnect(), { once: true });
      }

      homeTabs.forEach((tab) => {
        tab.addEventListener(
          'click',
          (event) => {
            event.preventDefault();
            activateHomeBand(tab.dataset.vzyHomeBandTab, { scroll: true });
          },
          { signal },
        );
      });

      document.addEventListener(
        'vzy:home:select-band',
        (event) => {
          const slug = event.detail?.slug || '';
          const matchingBand = bands.find((candidate) => candidate.band.dataset.vzyBandSlug === slug);
          if (matchingBand) {
            activateHomeBand(matchingBand.band.dataset.vzyBandKey, { focusTab: true, scroll: true });
          }
        },
        { signal },
      );
    }

    bands.forEach((bandState) => {
      const { band, toggle } = bandState;
      if (!toggle) return;

      toggle.addEventListener(
        'click',
        () => {
          const wasExpanded = bandState.expanded;
          if (!wasExpanded) materialize(bandState);
          bandState.expanded = !bandState.expanded;
          render({ announce: false });

          emitInteraction(root, bandState.expanded ? 'expand_band' : 'collapse_band', {
            band: band.dataset.vzyBandSlug || '',
          });

          if (wasExpanded) {
            band.scrollIntoView({
              behavior: REDUCED_MOTION_QUERY.matches ? 'auto' : 'smooth',
              block: 'start',
            });
            requestAnimationFrame(() => toggle.focus({ preventScroll: true }));
          }
        },
        { signal },
      );
    });

    categoryFilter?.addEventListener(
      'change',
      () => {
        state.category = categoryFilter.value;
        syncUrl();
        render();
        emitInteraction(root, 'filter_category', { value: state.category });
      },
      { signal },
    );

    availabilityFilter?.addEventListener(
      'change',
      () => {
        state.availableOnly = availabilityFilter.checked;
        syncUrl();
        render();
        emitInteraction(root, 'filter_availability', { value: state.availableOnly ? 'available' : 'all' });
      },
      { signal },
    );

    sortControl?.addEventListener(
      'change',
      () => {
        state.sort = sortControl.value;
        syncUrl();
        render();
        emitInteraction(root, 'sort', { value: state.sort });
      },
      { signal },
    );

    resetButtons.forEach((button) => {
      button.addEventListener(
        'click',
        () => {
          state.category = 'all';
          state.availableOnly = false;
          state.sort = 'recommended';
          if (categoryFilter) categoryFilter.value = 'all';
          if (availabilityFilter) availabilityFilter.checked = false;
          if (sortControl) sortControl.value = 'recommended';
          bands.forEach((bandState) => {
            bandState.expanded = false;
          });
          syncUrl();
          render();
          emitInteraction(root, 'reset');
          categoryFilter?.focus({ preventScroll: true });
        },
        { signal },
      );
    });

    const onViewportChange = () => render({ announce: false });
    MOBILE_QUERY.addEventListener('change', onViewportChange, { signal });

    if (toolbar) toolbar.hidden = false;
    render({ announce: false });
  }

  function destroyRoot(root) {
    root?.vzyBandsController?.abort();

    if (root) {
      root.querySelectorAll('template[data-vzy-band-deferred]').forEach((template) => {
        template.replaceWith(template.content);
      });
      root.querySelectorAll('[data-vzy-product-band], [data-vzy-band-item]').forEach((element) => {
        element.hidden = false;
      });
      const toolbar = root.querySelector('[data-vzy-toolbar]');
      if (toolbar) toolbar.hidden = true;
      delete root.dataset.vzyBandsReady;
      delete root.vzyBandsController;
    }
  }

  function init(scope = document) {
    if (scope.matches?.(ROOT_SELECTOR)) initRoot(scope);
    scope.querySelectorAll?.(ROOT_SELECTOR).forEach(initRoot);
  }

  document.addEventListener('shopify:section:load', (event) => init(event.target));
  document.addEventListener('shopify:section:unload', (event) => {
    if (event.target.matches?.(ROOT_SELECTOR)) destroyRoot(event.target);
    event.target.querySelectorAll?.(ROOT_SELECTOR).forEach(destroyRoot);
  });
  document.addEventListener('shopify:block:select', (event) => {
    const root = event.target.closest?.(ROOT_SELECTOR);
    if (!root || !event.detail?.blockId) return;

    const categoryFilter = root.querySelector('[data-vzy-category-filter]');
    const band = root.querySelector(`[data-vzy-band-key="${CSS.escape(event.detail.blockId)}"]`);
    if (!band) return;

    if (root.dataset.vzyPageType === 'index') {
      document.dispatchEvent(
        new CustomEvent('vzy:home:select-band', {
          detail: { slug: band.dataset.vzyBandSlug || '' },
        }),
      );
      return;
    }

    if (categoryFilter) {
      categoryFilter.value = event.detail.blockId;
      categoryFilter.dispatchEvent(new Event('change', { bubbles: true }));
    }
    band.scrollIntoView({ behavior: 'auto', block: 'start' });
  });

  window.VzyCollectionBands = { init, destroyRoot };
  init();
})();